import styled from '@emotion/styled';
import { Badge, BadgeProps, Box, Chip, ChipProps, colors, TextField } from '@mui/material';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { error, neutral } from '_lex/_foundations/phxColors';

interface ContentPageProps {
  customWidth?: number | string;
}

export const MuiContentEditable = styled(ContentEditable)({
  minHeight: 200,
  width: '100%',
  borderRadius: 5,
  position: 'relative',
  outline: 'none',
  '&[contenteditable=false]': {
    minHeight: 0,
  },
});

export const EditorPlaceholder = styled(Box)({
  position: 'absolute',
  top: 15,
  left: 10,
  userSelect: 'none',
  display: 'inline-block',
  pointerEvents: 'none',
});

export const ContentPageContainer = styled(Box)({
  padding: 36,
  maxHeight: 'calc(100vh - (2 * 64px))',
  minHeight: 0,
  overflowY: 'auto',
  containerType: 'inline-size',
});

export const ContentPage = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'customWidth',
})<ContentPageProps>(({ customWidth }) => ({
  margin: '0 auto',
  background: '#FFF',
  padding: 36,
  border: `solid 1px ${colors.grey[200]}`,
  fontSize: 16,
  width: '100%',
  maxWidth: customWidth || 900,
  boxSizing: 'border-box',
  overflowX: 'hidden',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  // Media queries for viewport-based responsiveness
  '@media (max-width: 900px)': {
    padding: '24px',
  },
  '@media (max-width: 600px)': {
    padding: '16px',
  },
  // Container queries for container-based responsiveness (when parent has container-type)
  '@container (max-width: 900px)': {
    padding: '24px',
  },
  '@container (max-width: 600px)': {
    padding: '16px',
  },
  '& .MuiTypography-h5': {
    fontSize: 27,
  },
  '& .MuiTypography-h6': {
    fontSize: 18,
  },
  // Ensure content inside respects container width
  '& > *': {
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  // Handle tables - make them scrollable within container
  '& .PhxEditorTheme__table': {
    maxWidth: '100% !important',
    width: '100% !important',
    tableLayout: 'auto',
    '&::-webkit-scrollbar': {
      height: '8px',
    },
  },
  // Handle images
  '& img': {
    maxWidth: '100%',
    height: 'auto',
  },
  // Handle code blocks
  '& .PhxEditorTheme__code': {
    maxWidth: '100%',
    overflowX: 'auto',
  },
  // Handle editor content
  '& .editor-container': {
    maxWidth: '100%',
    overflowX: 'hidden',
  },
  '& .editor-scroller': {
    maxWidth: '100%',
    overflowX: 'hidden',
  },
  '& .editor': {
    maxWidth: '100%',
  },
  '.text-entity': {
    padding: '2px 4px',
    borderRadius: 8,
    display: 'inline-flex',
    '&:before': {
      content: '"["',
    },
    '&:after': {
      content: '"]"',
    },
  },
}));

export const TitleTextField = styled(TextField)({
  paddingTop: 0,
  '& .MuiOutlinedInput-root': {
    borderColor: `${colors.grey[50]} !important`,
  },
  '& .MuiOutlinedInput-input': {
    fontSize: 20,
    fontWeight: 600,
    padding: 8,
  },
});

export const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    backgroundColor: `${error[50]}`,
    color: `${error[600]}`,
    position: 'static',
    top: 'auto',
    transform: 'none',
  },
}));

export const StyledChip = styled(Chip)<ChipProps>(() => ({
  backgroundColor: `${neutral[25]}`,
  borderRadius: 10,
  height: '22px',
  padding: '2px',
  minWidth: 'max-content',
  '& .MuiChip-label': {
    fontWeight: 400,
    whiteSpace: 'normal',
  },
  '& .MuiChip-icon': {
    marginRight: '0.025rem',
    borderRadius: '100%',
    height: '10px',
    width: '10px',
    border: `1px solid ${neutral[300]}`,
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
  },
}));
